import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { VolumeStatus, VolumeHealthStatus, StorageClassTier, AccessMode, VolumeFilterOptions } from '@/service/gpu-instance/volume-type';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface VolumeSearchProps {
  filters: VolumeFilterOptions;
  onFiltersChange: (filters: VolumeFilterOptions) => void;
  onSearch: (search: string) => void;
}

const statusOptions: { value: VolumeStatus; label: string }[] = [
  { value: VolumeStatus.Available, label: 'Available' },
  { value: VolumeStatus.InUse, label: 'In Use' },
  { value: VolumeStatus.Pending, label: 'Pending' },
  { value: VolumeStatus.Expanding, label: 'Expanding' },
  { value: VolumeStatus.Error, label: 'Error' },
];

const storageClassOptions: { value: StorageClassTier; label: string }[] = [
  { value: 'standard-ssd', label: 'Standard SSD' },
  { value: 'premium-ssd', label: 'Premium SSD' },
  { value: 'nvme', label: 'NVMe Ultra' },
  { value: 'archive-hdd', label: 'Archive HDD' },
];

const accessModeOptions: { value: AccessMode; label: string }[] = [
  { value: 'ReadWriteOnce', label: 'RWO' },
  { value: 'ReadWriteMany', label: 'RWX' },
  { value: 'ReadOnlyMany', label: 'ROX' },
  { value: 'ReadWriteOncePod', label: 'RWOP' },
];

const healthOptions: { value: VolumeHealthStatus; label: string }[] = [
  { value: VolumeHealthStatus.Healthy, label: 'Healthy' },
  { value: VolumeHealthStatus.Warning, label: 'Warning' },
  { value: VolumeHealthStatus.Degraded, label: 'Degraded' },
  { value: VolumeHealthStatus.Error, label: 'Error' },
];

export const VolumeSearch = ({ filters, onFiltersChange, onSearch }: VolumeSearchProps) => {
  const activeFilterCount = [
    filters.status?.length ?? 0,
    filters.storageClassTier?.length ?? 0,
    filters.accessMode?.length ?? 0,
    filters.healthStatus?.length ?? 0,
    filters.isAttached !== undefined ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const clearAllFilters = () => {
    onFiltersChange({});
  };

  const toggleArrayFilter = <T,>(
    key: keyof VolumeFilterOptions,
    value: T,
    currentArray?: T[]
  ) => {
    const current = currentArray ?? [];
    const newArray = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onFiltersChange({
      ...filters,
      [key]: newArray.length > 0 ? newArray : undefined,
    });
  };

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search volumes by name..."
          className="pl-9"
          value={filters.search ?? ''}
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>

      <div className="flex gap-2">
        <Select
          value={filters.isAttached === undefined ? 'all' : filters.isAttached ? 'attached' : 'detached'}
          onValueChange={(value) => {
            onFiltersChange({
              ...filters,
              isAttached: value === 'all' ? undefined : value === 'attached',
            });
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Attachment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Volumes</SelectItem>
            <SelectItem value="attached">Attached</SelectItem>
            <SelectItem value="detached">Detached</SelectItem>
          </SelectContent>
        </Select>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 w-5 rounded-full p-0 text-xs">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Filters</h4>
                {activeFilterCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-auto p-0 text-xs">
                    Clear all
                  </Button>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Status</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {statusOptions.map((option) => (
                      <label
                        key={option.value}
                        className="flex cursor-pointer items-center gap-1.5"
                      >
                        <Checkbox
                          checked={filters.status?.includes(option.value) ?? false}
                          onCheckedChange={() =>
                            toggleArrayFilter('status', option.value, filters.status)
                          }
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Storage Class</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {storageClassOptions.map((option) => (
                      <label
                        key={option.value}
                        className="flex cursor-pointer items-center gap-1.5"
                      >
                        <Checkbox
                          checked={filters.storageClassTier?.includes(option.value) ?? false}
                          onCheckedChange={() =>
                            toggleArrayFilter('storageClassTier', option.value, filters.storageClassTier)
                          }
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Access Mode</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {accessModeOptions.map((option) => (
                      <label
                        key={option.value}
                        className="flex cursor-pointer items-center gap-1.5"
                      >
                        <Checkbox
                          checked={filters.accessMode?.includes(option.value) ?? false}
                          onCheckedChange={() =>
                            toggleArrayFilter('accessMode', option.value, filters.accessMode)
                          }
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Health</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {healthOptions.map((option) => (
                      <label
                        key={option.value}
                        className="flex cursor-pointer items-center gap-1.5"
                      >
                        <Checkbox
                          checked={filters.healthStatus?.includes(option.value) ?? false}
                          onCheckedChange={() =>
                            toggleArrayFilter('healthStatus', option.value, filters.healthStatus)
                          }
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default VolumeSearch;
